import React from "react";
import { useTranslation } from "react-i18next";
import AnimatedModal from "../../../components/AnimatedModal";
import { Table } from "../../../components/Table";
import { Row } from "@tanstack/react-table";

interface CampaignCost {
  campaign_name: string;
  cost: string;
}

interface CampaignCountryCostModalProps {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  // Accept both possible data shapes for backward compatibility
  data?: {
    campaign_data?: CampaignCost[];
    total_campaign_cost?: string;
    status?: string;
    // legacy shape
    costs?: { campaign_name: string; cost_inr: number }[];
    total_cost_inr?: number;
  };
  countryName?: string;
}

const CampaignCountryCostModal: React.FC<CampaignCountryCostModalProps> = ({
  open,
  onClose,
  loading,
  data,
  countryName,
}) => {
  const { t } = useTranslation();

  // Support both new and legacy data shapes
  const campaigns =
    data?.campaign_data ||
    (data?.costs
      ? data.costs.map((c) => ({
          campaign_name: c.campaign_name,
          cost: String(c.cost_inr),
        }))
      : []);
  const totalCost =
    data?.total_campaign_cost ||
    (typeof data?.total_cost_inr === "number"
      ? parseFloat(data.total_cost_inr.toString()).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : undefined);

  const columns = [
    {
      accessorKey: "campaign_name",
      size: 80,
      header: t("apps.campaignCountryCost.campaignName"),
      cell: ({ row }: { row: Row<CampaignCost> }) => (
        <p className="break-words">{row.original.campaign_name}</p>
      ),
    },
    {
      accessorKey: "cost",
      size: 20,
      header: t("apps.campaignCountryCost.cost"),
      cell: ({ row }: { row: Row<CampaignCost> }) =>
        typeof row.original.cost === "string"
          ? row.original.cost
          : parseFloat(row.original.cost).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
    },
  ];

  return (
    <AnimatedModal
      show={open}
      onClose={onClose}
      title={`${t("apps.campaignCountryCost.title")}${
        countryName ? ` - ${countryName}` : ""
      }`}
      size="lg"
    >
      <div className="space-y-4">
        <Table
          data={campaigns}
          columns={columns}
          isLoading={loading}
          showPagination={false}
          showSearch={false}
          maxHeight="600px"
        />
        <div className="flex justify-between font-semibold px-2">
          <span>{t("apps.campaignCountryCost.totalCost")}</span>
          <span>{totalCost || "0"}</span>
        </div>
      </div>
    </AnimatedModal>
  );
};

export default CampaignCountryCostModal;
